import { CompletionSource } from '@codemirror/autocomplete';
import { javascript, javascriptLanguage } from '@codemirror/lang-javascript';
import { LanguageSupport, LRLanguage } from '@codemirror/language';
import { Prec } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view';
import { parseMixed } from '@lezer/common';
import { parser as javascriptTemplatedParser } from '@persona-id/codemirror-lang-javascript-templated';

import {
  javascriptKeywordCompletionSource,
  localIntellisenseCompletionSource,
} from 'dashboard/components-lib/CodeEditor/completionSources/javascript/localIntellisenseCompletionSource';

export function javascriptTemplated(jsCompletionSources: CompletionSource[] = []) {
  const javascriptLang = javascript();

  const parser = javascriptTemplatedParser.configure({
    wrap: parseMixed((node) => {
      return node.type.isTop
        ? {
            parser: javascriptLanguage.parser,
            overlay: (node) => node.type.name === 'JsTemplateBlock',
          }
        : null;
    }),
  });

  const language = LRLanguage.define({
    name: 'jstemplate',
    parser: parser,
    languageData: {
      commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
      closeBrackets: { brackets: ['(', '[', '{', "'", '"', '`'] },
    },
  });

  const bracesDecorator = new MatchDecorator({
    regexp: /{{|}}/g,
    decoration: Decoration.mark({ class: 'cm-persona-script-embed-braces' }),
  });

  const bracesHighlight = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = bracesDecorator.createDeco(view);
      }
      update(update: ViewUpdate) {
        this.decorations = bracesDecorator.updateDeco(update, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

  const jsCompletionExtensions = jsCompletionSources.flatMap((source) => {
    return [
      javascriptLang.language.data.of({
        autocomplete: source,
      }),
    ];
  });

  const languageSupport = () =>
    new LanguageSupport(
      language,
      [
        [
          language.data.of({
            autocomplete: localIntellisenseCompletionSource,
          }),
        ],
        [
          language.data.of({
            autocomplete: javascriptKeywordCompletionSource,
          }),
        ],
        [javascriptLang.support, ...jsCompletionExtensions],
        Prec.lowest([bracesHighlight]),
      ].flat(),
    );

  return [languageSupport()];
}
